// src/components/modals/FileModals/RenameModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';

const RenameModal = ({ show, onHide, onRename, fileItem }) => {
  const [newName, setNewName] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [error, setError] = useState('');

  // Update the input when fileItem changes
  useEffect(() => {
    if (fileItem && show) {
      // Extract filename without extension for easier editing
      const fileName = fileItem.name;
      const lastDotIndex = fileName.lastIndexOf('.');
      
      if (lastDotIndex > 0) {
        // File has extension - select just the name part
        setNewName(fileName.substring(0, lastDotIndex));
      } else {
        // No extension or hidden file
        setNewName(fileName);
      }
      setError('');
    }
  }, [fileItem, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newName.trim()) {
      setError('File name cannot be empty');
      return;
    }

    // Validate filename (basic validation)
    const invalidChars = /[<>:"/\\|?*]/;
    if (invalidChars.test(newName)) {
      setError('File name contains invalid characters');
      return;
    }

    if (newName.trim() === getNameWithoutExtension(fileItem.name)) {
      setError('New name is the same as current name');
      return;
    }

    setIsRenaming(true);
    setError('');

    try {
      // Reconstruct filename with original extension if it had one
      const originalName = fileItem.name;
      const lastDotIndex = originalName.lastIndexOf('.');
      const finalNewName = lastDotIndex > 0 
        ? `${newName.trim()}${originalName.substring(lastDotIndex)}`
        : newName.trim();

      const success = await onRename(fileItem, finalNewName);
      
      if (success) {
        onHide();
      }
    } catch (err) {
      setError(err.message || 'Failed to rename file');
    } finally {
      setIsRenaming(false);
    }
  };

  const getNameWithoutExtension = (fileName) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  };

  const getFileExtension = (fileName) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Rename File</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {fileItem && (
            <div className="mb-3">
              <p className="text-muted mb-2">
                Current name: <strong>{fileItem.name}</strong>
              </p>
              
              <Form.Group>
                <Form.Label>New name:</Form.Label>
                <div className="d-flex align-items-center">
                  <Form.Control
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isRenaming}
                    placeholder="Enter new file name"
                    autoFocus
                    className="me-2"
                  />
                  {getFileExtension(fileItem.name) && (
                    <span className="text-muted">
                      {getFileExtension(fileItem.name)}
                    </span>
                  )}
                </div>
                <Form.Text className="text-muted">
                  {getFileExtension(fileItem.name) 
                    ? 'Only the name part will be changed, extension will remain the same.'
                    : 'Enter the complete new name for this file.'
                  }
                </Form.Text>
              </Form.Group>

              {error && (
                <div className="alert alert-danger mt-2" role="alert">
                  {error}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isRenaming}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={isRenaming || !newName.trim()}
          >
            {isRenaming ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Renaming...
              </>
            ) : (
              'Rename'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

RenameModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  fileItem: PropTypes.object
};

export default RenameModal;